import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { loadThree } from '../utils/threeLoader';
import api from '../services/api';
import { getCharactersForLanguage } from '../utils/alphabets';
import './HomePage.css';

// Import des images de drapeaux
import flagFR from '../assets/flags/FR.jpg';
import flagEN from '../assets/flags/EN.jpg';
import flagDE from '../assets/flags/DE.jpg';
import flagES from '../assets/flags/ES.jpg';
import flagIT from '../assets/flags/IT.jpg';
import flagPT from '../assets/flags/PT.jpg';
import flagTR from '../assets/flags/TR.jpg';
import flagPL from '../assets/flags/PL.jpg';
import flagRU from '../assets/flags/RU.jpg';
import flagUK from '../assets/flags/UK.jpg';

const HomePage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { isAuthenticated, loading } = useAuth();
  const canvasRef = useRef(null);
  const tubesAppRef = useRef(null);
  const [showcaseData, setShowcaseData] = useState([]);
  const [showcaseLoading, setShowcaseLoading] = useState(true);
  const [showcaseError, setShowcaseError] = useState(null);

  useEffect(() => {
    const fetchShowcase = async () => {
      try {
        setShowcaseError(null);
        const response = await api.get('/matrices/showcase/');
        setShowcaseData(response.data.results || []);
      } catch (error) {
        console.error('Error loading showcase:', error);
        setShowcaseError(error.message || 'Erreur de chargement');
        setShowcaseData([]);
      } finally {
        setShowcaseLoading(false);
      }
    };
    fetchShowcase();
  }, []);

  const languages = [
    { code: 'fr', name: 'Français', isoCode: 'FR', flagImage: flagFR },
    { code: 'en', name: 'English', isoCode: 'EN', flagImage: flagEN },
    { code: 'de', name: 'Deutsch', isoCode: 'DE', flagImage: flagDE },
    { code: 'es', name: 'Español', isoCode: 'ES', flagImage: flagES },
    { code: 'it', name: 'Italiano', isoCode: 'IT', flagImage: flagIT },
    { code: 'pt', name: 'Português', isoCode: 'PT', flagImage: flagPT },
    { code: 'tr', name: 'Türkçe', isoCode: 'TR', flagImage: flagTR },
    { code: 'pl', name: 'Polski', isoCode: 'PL', flagImage: flagPL },
    { code: 'ru', name: 'Русский', isoCode: 'RU', flagImage: flagRU },
    { code: 'uk', name: 'Українська', isoCode: 'UK', flagImage: flagUK },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Nettoyer l'instance existante avant d'en créer une nouvelle
    if (tubesAppRef.current && tubesAppRef.current.dispose) {
      tubesAppRef.current.dispose();
      tubesAppRef.current = null;
    }

    let isMounted = true;
    let handleClick = null;

    // Fonction pour générer des couleurs aléatoires
    const randomColors = (count) => {
      return new Array(count)
        .fill(0)
        .map(() => "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'));
    };

    // Charger dynamiquement le module TubesCursor
    const loadTubesCursor = async () => {
      try {
        // S'assurer que Three.js est chargé avant de charger la bibliothèque
        await loadThree();
        
        const module = await import('https://cdn.jsdelivr.net/npm/threejs-components@0.0.19/build/cursors/tubes1.min.js');
        const TubesCursor = module.default;
        
        // Vérifier isMounted AVANT de créer l'instance
        if (!isMounted) {
          return;
        }
        
        const app = TubesCursor(canvas, {
          tubes: {
            colors: ["#f967fb", "#53bc28", "#6958d5"],
            lights: {
              intensity: 200,
              colors: ["#83f36e", "#fe8a2e", "#ff008a", "#60aed5"]
            }
          }
        });

        // Vérifier à nouveau isMounted après création (au cas où le composant se démonte pendant la création)
        if (!isMounted) {
          if (app && app.dispose) {
            app.dispose();
          }
          return;
        }

        tubesAppRef.current = app;

        // Gestionnaire de clic pour changer les couleurs
        handleClick = () => {
          if (tubesAppRef.current && isMounted) {
            const colors = randomColors(3);
            const lightsColors = randomColors(4);
            console.log(colors, lightsColors);
            tubesAppRef.current.tubes.setColors(colors);
            tubesAppRef.current.tubes.setLightsColors(lightsColors);
          }
        };

        document.body.addEventListener('click', handleClick);
      } catch (error) {
        console.error('Error loading TubesCursor:', error);
      }
    };

    loadTubesCursor();

    return () => {
      isMounted = false;
      
      // Nettoyer l'event listener
      if (handleClick) {
        document.body.removeEventListener('click', handleClick);
      }
      
      // Nettoyer l'app depuis le ref (fonctionne même si l'app n'est pas encore créée)
      if (tubesAppRef.current && tubesAppRef.current.dispose) {
        tubesAppRef.current.dispose();
        tubesAppRef.current = null;
      }
    };
  }, []);

  const handleLanguageSelect = (languageCode) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('language', languageCode);
    
    // Si l'utilisateur est déjà connecté, rediriger vers le dashboard
    // Sinon, rediriger vers la page de connexion
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="homepage">
      <canvas ref={canvasRef} className="homepage-canvas"></canvas>
      
      <div className="homepage-content">
        <div className="homepage-heading">
          <h1 className="homepage-title">SYNESTESIA</h1>
          <h2 className="homepage-subtitle">EXPRESS YOUR SINGULARITY</h2>
        </div>
        
        <div className="homepage-main-area">
        <div className="language-selector">
          <div className="language-grid">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className="language-button"
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <div className="language-flag-container">
                  <div 
                    className="language-flag-mask"
                    style={{ backgroundImage: `url(${lang.flagImage})` }}
                  >
                    {lang.isoCode}
                  </div>
                </div>
                <span className="language-name">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="showcase-section">
          <h3 className="showcase-title">ShowCase</h3>
          {showcaseLoading ? (
            <p className="showcase-message">Chargement...</p>
          ) : showcaseError ? (
            <p className="showcase-message showcase-error">
              {showcaseError} — Vérifiez que le backend tourne sur {process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}
            </p>
          ) : showcaseData.length === 0 ? (
            <p className="showcase-message">Aucune matrice à afficher pour le moment</p>
          ) : (
            <div className="showcase-rows">
              {showcaseData.map((item, index) => {
                const { prenom, matrice } = item;
                const langue = matrice?.langue || 'fr';
                const characters = getCharactersForLanguage(langue);
                const letters = characters.letters;
                const numbers = characters.numbers;
                return (
                  <div key={matrice?.id || index} className="showcase-row">
                    <div className="showcase-prenom">{prenom}</div>
                    <div className="showcase-letters-chiffres">
                    <div className="showcase-alphabet">
                      {letters.map((lettre) => {
                        const couleur = matrice?.lettres_couleurs?.[lettre] || '#CCCCCC';
                        return (
                          <span
                            key={lettre}
                            className="showcase-letter"
                            style={{ color: couleur }}
                            title={`${lettre}: ${couleur}`}
                          >
                            {lettre}
                          </span>
                        );
                      })}
                    </div>
                    <div className="showcase-chiffres">
                      {numbers.map((chiffre) => {
                        const couleur = matrice?.chiffres_couleurs?.[chiffre] || '#CCCCCC';
                        return (
                          <span
                            key={chiffre}
                            className="showcase-chiffre"
                            style={{ color: couleur }}
                            title={`${chiffre}: ${couleur}`}
                          >
                            {chiffre}
                          </span>
                        );
                      })}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
        
        <div className="homepage-navigation">
          <Link to="/story" className="homepage-footer-link">
            Story
          </Link>
          <Link to="/contact" className="homepage-footer-link">
            Contact
          </Link>
        </div>
      </div>
      <footer className="homepage-empty-footer"></footer>
    </div>
  );
};

export default HomePage;

